// src/MyCompensations.js - DASHBOARD DEDICATA AI MEDICI (Sola Lettura)
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { db } from './firebase';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { useUser } from './UserContext';
import { format } from 'date-fns';
import { Calculator, FileText, Clock, Printer, ExternalLink } from 'lucide-react';

export default function MyCompensations() {
    const { userProfile } = useUser();
    const centerId = userProfile?.centerId;

    // Helper per il mese precedente
    const getPrevMonth = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return format(d, 'yyyy-MM');
    };

    const [viewMonth, setViewMonth] = useState(getPrevMonth());
    const [selectedTally, setSelectedTally] = useState(null);

    // Fetch Cedolini per il mese selezionato, filtrati SOLO per questo medico
    const { data: tallies = [], isLoading } = useQuery({
        queryKey: ['myTallies', centerId, viewMonth, userProfile?.uid],
        queryFn: async () => {
            // 🎯 FIX CHIRURGICO: Troviamo prima il VERO ID del documento Medico
            // corrispondente a questo utente loggato (authUid)
            const doctorQuery = query(
                collection(db, 'centers', centerId, 'doctors'), 
                where('authUid', '==', userProfile.uid)
            );
            const doctorSnap = await getDocs(doctorQuery);
            
            // Se troviamo il medico, usiamo il suo ID documento, altrimenti fallback sull'UID
            const realDoctorId = !doctorSnap.empty ? doctorSnap.docs[0].id : userProfile.uid;

            // 🎯 Ora facciamo la query dei cedolini usando il vero ID
            const q = query(
                collection(db, 'centers', centerId, 'doctorTallies'),
                where('monthId', '==', viewMonth),
                where('doctorId', '==', realDoctorId)
            );
            const snap = await getDocs(q);
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        },
        enabled: !!centerId && !!viewMonth && !!userProfile?.uid
    });

    // Funzione di stampa (identica a quella dell'Admin, ma usabile dal medico per le sue fatture/ricevute)
    const handlePrintTally = (tally) => {
        const details = tally.details || [];
        const isHourly = tally.paymentType === 'hourly';

        const buildTable = () => {
            if (details.length === 0) return "<p>Nessuna prestazione rilevata.</p>";
            
            // 🎯 FIX CHIRURGICO: Rimuoviamo il filtro. Mostriamo tutto, ma segnaliamo cosa è sospeso.
            const rows = details.map(d => {
                const isIncluded = d.pendingIncluded !== false; // Controlla se l'admin lo ha incluso
                
                let badge = "";
                let valToDisplay = parseFloat(d.price || 0).toFixed(2);
                if (d.isPartOfCombo) { badge = " <b>[COMBO]</b>"; valToDisplay = parseFloat(d.finalPrice || d.price).toFixed(2); }
                if (d.isCoveredByPackage) { badge = " <b>[PACCHETTO]</b>"; valToDisplay = parseFloat(d.gettoneMedico || d.price).toFixed(2); }
                
                // Logica stato per la stampa
                let statoCell = "";
                if (!isIncluded) {
                    statoCell = '<span style="color: #dc2626; font-weight: bold;">Sospeso</span>';
                } else if (d.isPaid) {
                    statoCell = 'Pagato';
                } else if (d.pendingType === 'fattura_insoluta') {
                    statoCell = 'Fatt. Insoluta';
                } else {
                    statoCell = 'Incluso';
                }

                const fatturaCell = d.invoiceNumber ? `N. ${d.invoiceNumber}` : '-';
                const durataCell = isHourly ? `${d.appointmentMinutes ? (d.appointmentMinutes / 60).toFixed(2) + 'h' : '-'}` : `€ ${valToDisplay}`;
                
                const percSpettanzaCells = isHourly 
                    ? `<td>€ ${parseFloat(d.price || 0).toFixed(2)}</td>` 
                    : `<td>${d.percentage}%</td><td><strong>€ ${parseFloat(d.doctorCut || 0).toFixed(2)}</strong></td>`;
                
                // Se non è incluso, facciamo la riga grigina/rossiccia
                const rowStyle = isIncluded ? "background: white;" : "background: #fff1f2; color: #64748b;";

                return `
                    <tr style="${rowStyle}">
                        <td>${format(new Date(d.date), 'dd/MM/yyyy HH:mm')}</td>
                        <td>${d.patientName}</td>
                        <td>${d.serviceName}${badge}</td>
                        <td>${durataCell}</td>
                        ${percSpettanzaCells}
                        <td>${statoCell}</td>
                        <td>${fatturaCell}</td>
                    </tr>
                `;
            }).join('');

            const tableHeaders = isHourly 
                ? `<tr><th>Data</th><th>Paziente</th><th>Prestazione</th><th>Durata</th><th>Importo Paz.</th><th>Stato Pagamento</th><th>N. Fattura</th></tr>` 
                : `<tr><th>Data</th><th>Paziente</th><th>Prestazione</th><th>Base Calcolo</th><th>% Medico</th><th>Quota Spettante</th><th>Stato Pagamento</th><th>N. Fattura</th></tr>`; 
            
            return `<table><thead>${tableHeaders}</thead><tbody>${rows}</tbody></table>`;
        };
        
        const html = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8"><title>Distinta Compenso</title><style>body { font-family: Arial, sans-serif; padding: 40px; } table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; } th, td { padding: 8px; border: 1px solid #cbd5e1; text-align: left; } th { background-color: #f1f5f9 } .summary { background: #f8fafc; padding: 20px; margin-bottom: 20px; border-left: 4px solid #2563eb }</style></head><body><h1>Distinta Compensi</h1><p><strong>Medico:</strong> ${tally.doctorName}<br><strong>Mese:</strong> ${tally.monthId}</p><div class="summary"><h2>TOTALE: € ${parseFloat(tally.totalAmount || 0).toFixed(2)}</h2></div>${buildTable()}</body></html>`;
        
        const win = window.open('', '_blank');
        win.document.write(html);
        win.document.close();
        setTimeout(() => win.print(), 500);
    };

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Calculator size={24} color="#2563eb" /> I Miei Compensi
            </h2>

            {/* SELETTORE MESE */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', backgroundColor: 'white', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '5px' }}>Seleziona Mese:</label>
                    <input 
                        type="month" value={viewMonth} onChange={(e) => setViewMonth(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                </div>
            </div>

            {/* LISTA CEDOLINI */}
            {isLoading ? <p>Caricamento in corso...</p> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {tallies.length === 0 ? (
                        <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: '#94a3b8', backgroundColor: 'white', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                            <FileText size={48} style={{ opacity: 0.3, marginBottom: '10px' }} />
                            <p>Nessun compenso trovato per il mese di {viewMonth}.</p>
                        </div>
                    ) : tallies.map(tally => (
                        <div 
                            key={tally.id} onClick={() => setSelectedTally(tally)}
                            style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', cursor: 'pointer', border: `1px solid ${tally.status === 'approved' ? '#86efac' : tally.status === 'rejected' ? '#fca5a5' : '#e2e8f0'}`, borderTop: `4px solid ${tally.status === 'approved' ? '#22c55e' : tally.status === 'rejected' ? '#ef4444' : '#f59e0b'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Mese: {tally.monthId}</h3>
                                <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '3px 8px', borderRadius: '20px', backgroundColor: tally.status === 'approved' ? '#dcfce7' : tally.status === 'rejected' ? '#fee2e2' : '#fef3c7', color: tally.status === 'approved' ? '#166534' : tally.status === 'rejected' ? '#991b1b' : '#92400e' }}>
                                    {tally.status === 'approved' ? 'APPROVATO' : tally.status === 'rejected' ? 'DA REVISIONARE' : 'IN ATTESA'}
                                </span>
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <div style={{ fontSize: '22px', fontWeight: '900', color: '#16a34a' }}>
                                    € {parseFloat(tally.totalAmount || 0).toFixed(2)}
                                </div>
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <Clock size={12} /> {tally.details?.length || 0} prestazioni elaborate
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* MODALE DI DETTAGLIO */}
            {selectedTally && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setSelectedTally(null)}>
                    <div style={{ backgroundColor: 'white', width: '95%', maxWidth: '1000px', maxHeight: '90vh', borderRadius: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
                        
                        <div style={{ padding: '20px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                            <div>
                                <h2 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Dettaglio Compensi: {selectedTally.monthId}</h2>
                                <span style={{ color: '#64748b', fontSize: '13px' }}>Modello di calcolo: {selectedTally.paymentType}</span>
                            </div>
                            <button onClick={() => setSelectedTally(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
                        </div>

                        <div style={{ padding: '25px', overflowY: 'auto', flex: 1 }}>
                            <div style={{ backgroundColor: '#10b981', padding: '15px', borderRadius: '8px', color: 'white', marginBottom: '20px', width: 'fit-content' }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.9 }}>Totale Spettante</div>
                                <div style={{ fontSize: '22px', fontWeight: '900' }}>€ {parseFloat(selectedTally.totalAmount || 0).toFixed(2)}</div>
                            </div>

                            <h4 style={{ margin: '0 0 15px 0', color: '#334155' }}>Analitico Prestazioni</h4>
                            
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                            <th style={{ padding: '12px' }}>Data</th>
                                            <th style={{ padding: '12px' }}>Paziente</th>
                                            <th style={{ padding: '12px' }}>Servizio</th>
                                            <th style={{ padding: '12px' }}>Valore</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>%</th>
                                            <th style={{ padding: '12px' }}>Spettanza</th>
                                            <th style={{ padding: '12px' }}>Stato</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedTally.details.map((d, i) => {
                                            // 🎯 FIX CHIRURGICO: Calcoliamo se la riga è inclusa o sospesa
                                            const isIncluded = d.pendingIncluded !== false;
                                            
                                            return (
                                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: isIncluded ? 'transparent' : '#fff1f2' }}>
                                                    <td style={{ padding: '10px', whiteSpace: 'nowrap', color: '#475569', fontSize: '12px', textDecoration: isIncluded ? 'none' : 'line-through' }}>{format(new Date(d.date), 'dd/MM/yy HH:mm')}</td>
                                                    <td style={{ padding: '10px', fontWeight: '600', color: isIncluded ? '#1e293b' : '#94a3b8' }}>{d.patientName}</td>
                                                    <td style={{ padding: '10px', color: isIncluded ? '#334155' : '#94a3b8' }}>{d.serviceName}</td>
                                                    <td style={{ padding: '10px', fontWeight: '600', color: isIncluded ? '#475569' : '#94a3b8' }}>€ {parseFloat(d.price || 0).toFixed(2)}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center', color: '#64748b', fontWeight: 'bold' }}>{d.percentage}%</td>
                                                    <td style={{ padding: '10px', fontWeight: '900', color: isIncluded ? '#2563eb' : '#94a3b8', fontSize: '14px' }}>€ {parseFloat(d.doctorCut || 0).toFixed(2)}</td>
                                                    <td style={{ padding: '10px' }}>
                                                        {isIncluded ? (
                                                            <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#dcfce7', padding: '3px 6px', borderRadius: '4px' }}>Incluso</span>
                                                        ) : (
                                                            <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#fee2e2', padding: '3px 6px', borderRadius: '4px' }}>Sospeso</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div style={{ padding: '20px 30px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <button onClick={() => handlePrintTally(selectedTally)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: 'white', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                                <Printer size={16} /> Stampa Dettaglio
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}